import React, { useMemo } from 'react';

import { Box } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { AreaClosed, Bar, Line, LinePath } from '@visx/shape';
import { timeFormat } from 'd3-time-format';

import { useXYChartTooltipInPortal } from '../../../../../../Shared/components-v2/charts/components/XYChartTooltip';
import {
  IndexDomainScale,
  useChartHighlight,
} from '../../../../../../Shared/components-v2/charts/hooks/useChartHighlight';
import { useTickLabelProps } from '../../../../../../Shared/components-v2/charts/hooks/useTickLabelProps';
import { PerformanceHistoryItemFragment } from '../../../../../../Shared/graphql/performanceHistoryItem.generated';

type Props = {
  data: PerformanceHistoryItemFragment[];
  height: number;
  width: number;
  disabled?: boolean;
  onHighlightChange: (highlightData: PerformanceHistoryItemFragment | null) => void;
};

const defaultMargin = { top: 10, right: 1, bottom: 20, left: 1 };
const yAxisScaleCoefficient = 1.05;

const formatDate = timeFormat('%b %d, %Y');

const maxOfAllBalanceAndInvested = (data: PerformanceHistoryItemFragment[]) => {
  return Math.max(...data.map(d => Math.max(d.balance.cents, d.invested.cents)));
};

export const PerformanceChart: React.FC<Props> = ({ data, width, height, disabled = false, onHighlightChange }) => {
  const xMax = Math.max(0, width - defaultMargin.left - defaultMargin.right);
  const yMax = Math.max(0, height - defaultMargin.top - defaultMargin.bottom);

  const performanceScale = useMemo(
    () => scaleLinear({ range: [yMax, 0], domain: [0, maxOfAllBalanceAndInvested(data) * yAxisScaleCoefficient] }),
    [yMax, data]
  );

  const dateScale = useMemo(() => scaleLinear({ range: [0, xMax], domain: [0, data.length - 1] }), [xMax, data.length]);

  const chartHighlight = useChartHighlight<PerformanceHistoryItemFragment, IndexDomainScale>({
    dateScale,
    data,
    xAccessor: i => data.indexOf(i),
    disabled,
    marginLeft: defaultMargin.left,
    onHighlightChange,
  });
  const { highlightData, handleHighlightUpdate, handleHighlightCancel } = chartHighlight;

  const { XYChartTooltipInPortal, containerRef } = useXYChartTooltipInPortal();
  const tickLabelProps = useTickLabelProps({ highlightData });

  return (
    <Box css={{ position: 'relative', width: '100%', maxWidth: width }}>
      <svg width={width} height={height} ref={containerRef} style={{ maxWidth: '100%' }}>
        <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
        <Group left={defaultMargin.left} top={defaultMargin.top}>
          <GridRows
            scale={performanceScale}
            width={xMax}
            height={yMax}
            strokeDasharray="4,4"
            stroke={token('colors.border.weak')}
            pointerEvents="none"
            numTicks={5}
          />
          {/* X Axis */}
          <AxisBottom
            scale={dateScale}
            top={yMax}
            tickValues={[0, data.length - 1]}
            tickFormat={(v, i) => (i === 1 ? 'Today' : formatDate(data[v.valueOf()].date))}
            hideTicks={true}
            rangePadding={0}
            labelOffset={0}
            axisLineClassName={css({ stroke: 'border' })}
            tickLabelProps={tickLabelProps}
          />
          <AreaClosed
            className={css({ fill: 'dataviz.blue.overlay' })}
            data={data}
            yScale={performanceScale}
            x={(_, i) => dateScale(i)}
            y={d => performanceScale(d.balance.cents)}
          />
          {/*  Net deposits line */}
          <LinePath
            className={css({ stroke: 'border.strong', strokeWidth: 'thin' })}
            data={data}
            strokeLinejoin="round"
            x={(_, i) => dateScale(i)}
            y={d => performanceScale(d.invested.cents)}
          />
          {/*  Balance line */}
          <LinePath
            className={css({ stroke: 'dataviz.blue.2', strokeWidth: '2px' })}
            data={data}
            strokeLinejoin="round"
            x={(_, i) => dateScale(i)}
            y={d => performanceScale(d.balance.cents)}
          />
          <Bar
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onTouchStart={handleHighlightUpdate}
            onTouchMove={handleHighlightUpdate}
            onMouseMove={handleHighlightUpdate}
            onMouseLeave={handleHighlightCancel}
          />
          {highlightData && (
            <g>
              <Line
                from={{ x: highlightData.highlightLeft, y: -10 }}
                to={{ x: highlightData.highlightLeft, y: height - defaultMargin.bottom - defaultMargin.top }}
                stroke={token('colors.border.strongest')}
                strokeWidth={1}
                pointerEvents="none"
              />
              <circle
                cx={highlightData.highlightLeft}
                cy={height - defaultMargin.bottom - defaultMargin.top}
                r={1.5}
                fill={token('colors.border.strongest')}
                stroke={token('colors.border.strongest')}
                strokeWidth={2}
                pointerEvents="none"
              />
            </g>
          )}
        </Group>
      </svg>
      {highlightData && (
        <Box>
          <XYChartTooltipInPortal tooltipLeft={highlightData.highlightLeft} chartHeight={height} margin={defaultMargin}>
            {formatDate(highlightData.highlight.date)}
          </XYChartTooltipInPortal>
        </Box>
      )}
    </Box>
  );
};

export { formatDate as formatPerformanceDate };
